import React, { FC, useContext, useRef } from 'react';
import _                                 from 'underscore';
import { List, Popup, Segment }          from 'semantic-ui-react';

import { DecisionEngineQueries, SystemAccountingQueries }                         from '../../../../../queries';
import { CorvalProjectActivity, FlexAlign, FlexJustify, KeyedObject, Size, Uuid } from '../../../../../types';
import { ListFromQuery, ListFromQueryItemProps }                                  from '../../../../components/from-query/ListFromQuery';
import { ProjectContext }                                                         from '../../ProjectIndexPage';
import { ACTIVITY_CRITICALITY, ACTIVITY_STATUS }                                  from '../../../../../constants/ProductEnums';
import { TextLink }                                                               from '../../../../components/display/TextLink';
import { maybeStaticVersionField, runIfFunction }                                 from '../../../../../util';
import { FinePrintMessage }                                                       from '../../../../components/util/messaging/FinePrintMessage';
import { InlineLoadingIndicatorLabel }                                            from '../../../../components/util/loading/InlineLoadingIndicatorLabel';
import { useFeatureFlags, useProjectActivities }                                  from '../../../../../hooks';
import { Cell, Grid }                                                             from 'styled-css-grid';
import { ActivityCriticalityLabel }                                               from '../../../../components/activities/ActivityCriticalityLabel';
import { ProjectService, TimeService }                                            from '../../../../../services';
import { ActivityLateCompletionMessage }                                          from '../../../../components/activities/ActivityLateCompletionMessage';
import { Flexbox, FlexItem }                                                      from '../../../../containers';
import { ActivityTimingLabel }                                                    from '../../../../components/activities/ActivityTimingLabel';


interface Props {
    id: Uuid,

    onClick?( item: CorvalProjectActivity ): void,
}

const defaultProps: Props = {
    id:      null,
    onClick: null,
}

const ObjectiveDetailRelatedProjectActivityList: FC<Props> = ( props ) => {

    let { projectId, projectDictionary, isCorvalManaged, projectVersionId } = useContext( ProjectContext ),
        featureFlags                                                        = useFeatureFlags(),
        { hasLateCompletion }                                               = useProjectActivities(),
        projectTimelineFields                                               = useRef( [] );

    return (

        <ListFromQuery
            query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
				id
				startTimeUtc
				endTimeUtc
				activity{ id displayName defaultCriticality projectTimingCompletionMilestone }
				project { ${ SystemAccountingQueries.fragments.PROJECTS.TIMELINE_FIELDS_AND_DEFINED_TIMINGS( projectVersionId ) } }
			`, projectVersionId ) }
            queryOptions={ {
                variables: {
                    projectId,
                    projectVersionId,
                    where: {
                        and: _.compact( [
                            { subObjectiveId: { eq: props.id } },
                            isCorvalManaged ? { completionState: { neq: ACTIVITY_STATUS.EXCLUDED } } : null
                        ] )
                    }
                },

                // since statuses my change, we need to not rely on cache here.
                fetchPolicy: 'network-only'
            } }
            queryResultObject={ data => {

                const projectActivitiesField = maybeStaticVersionField( 'projectActivites', !isCorvalManaged );

                const activityCollection = data[ projectActivitiesField ];

                // create localized copy of project timeline fields
                if( !_.isEmpty( activityCollection ) ){
                    projectTimelineFields.current = ProjectService.getTimelineDataObjectFromProjectFields( activityCollection[ 0 ].project, projectDictionary );
                }

                // return activities sorted by start time
                return _.chain( activityCollection )
                        .sortBy( a => a.startTimeUtc )
                        .value()
            } }
            containerComponent={ ( props: KeyedObject ) => (
                <List relaxed verticalAlign={ 'top' }>
                    { props.children }
                </List>
            ) }
            errorComponent={ ( errors: any, query?: any ) => (
                <Segment size={ 'small' } basic secondary>
                    <FinePrintMessage>Activities unavailable.</FinePrintMessage>
                </Segment>
            ) }
            loadingComponent={ () => (
                <InlineLoadingIndicatorLabel label={ 'Loading activities...' }/>
            ) }
            itemComponent={ ( itemProps: ListFromQueryItemProps<CorvalProjectActivity> ) => {

                const projectActivity                      = itemProps.item,
                      { projectTimingCompletionMilestone } = projectActivity.activity

                const criticalityValue = projectActivity.activity.defaultCriticality as unknown as ACTIVITY_CRITICALITY,
                      isCritical       = ( criticalityValue === ACTIVITY_CRITICALITY.CRITICAL );

                const hasLateCompletionTiming = hasLateCompletion(
                    projectActivity.endTimeUtc,
                    projectTimingCompletionMilestone,
                    projectTimelineFields.current
                );

                const completionTimelineField = ProjectService.getTimelineDataObjectFromSet( projectTimingCompletionMilestone, projectTimelineFields.current ),
                      completeByLabel         = completionTimelineField?.label ?? null,
                      completeByDate          = completionTimelineField?.value ?? TimeService.ISOtoDisplayDate( projectActivity.endTimeUtc );

                return (
                    <List.Item key={ projectActivity.id }>
                        <List.Icon name={ 'lightning' } color={ 'teal' }/>
                        <List.Content>
                            <Grid columns={ `1fr minmax(60px,auto)` } rows={ `auto auto` } rowGap={ '2px' }>
                                <Cell>

                                    <List.Header>
                                        <>
                                            { _.isFunction( props.onClick )
                                              ? <TextLink onClick={ () => runIfFunction( props.onClick, projectActivity ) } className={ 'text--colored-black' }>{ projectActivity.activity.displayName }</TextLink>
                                              : projectActivity.activity.displayName
                                            }
                                        </>
                                    </List.Header>

                                </Cell>
                                <Cell className={ 'text-right ' }>

                                    {/* Status Icon / Indicator Area */ }
                                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.END } gutterSize={ Size.XS } noWrap={ true }>

                                        {/* Late Completion Indicator */ }
                                        { hasLateCompletionTiming && (
                                            <FlexItem>
                                                <Popup
                                                    size={ 'small' }
                                                    position={ 'top right' }
                                                    content={ <ActivityLateCompletionMessage completeByDate={ completeByDate } completeByLabel={ completeByLabel }/> }
                                                    trigger={ <ActivityTimingLabel size={ 'mini' } value={ 'late-completion' }/> }/>
                                            </FlexItem>
                                        ) }

                                        {/* Critical Indicator */ }
                                        { featureFlags.enableProjectActivityCriticalityViewsAndTools && isCritical && (
                                            <FlexItem>
                                                <ActivityCriticalityLabel size={ 'mini' } value={ criticalityValue }/>
                                            </FlexItem>
                                        ) }

                                    </Flexbox>
                                </Cell>
                                <Cell width={ 3 }>
                                    <List.Description>
                                        <FinePrintMessage>{ projectActivity.activity.description }</FinePrintMessage>
                                    </List.Description>
                                </Cell>
                            </Grid>
                        </List.Content>
                    </List.Item>

                )
            } }
            noItemsComponent={ () => null }>

        </ListFromQuery>
    );
};

ObjectiveDetailRelatedProjectActivityList.defaultProps = defaultProps;

export { ObjectiveDetailRelatedProjectActivityList }
